"use client";

import { getPostHogEventContext } from "@/lib/posthog-browser";
import { POSTHOG_EVENT_CONTEXT_FORM_FIELD } from "@/lib/posthog-constants";

import { useState, useTransition } from "react";
import { Minus, Plus } from "lucide-react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";

import { addPdpLineToCheckout } from "./add-to-cart-action";
import { useGateCartAction } from "@/hooks/use-gate-cart-action";
import type { BacQtyLabel } from "./bac-cross-sell-pricing";

interface BacCrossSellQtyPickerProps {
	channel: string;
	addLabel: string;
	/** Saleor variant id for the single-vial BAC variant. Quantity is sent separately. */
	variantId: string;
	/** Pre-resolved (reference-currency-aware) cart quantity labels, computed server-side. */
	qtyLabels: BacQtyLabel[];
	// productSlug + locale reserved for future use (e.g. analytics, redirect)
	productSlug: string;
	locale: string;
}

export function BacCrossSellQtyPicker({
	channel,
	addLabel,
	variantId,
	qtyLabels,
	locale,
}: BacCrossSellQtyPickerProps) {
	const [qty, setQty] = useState(qtyLabels[0]?.qty ?? 1);
	const [isPending, startTransition] = useTransition();
	const t = useTranslations("product");
	const gateAction = useGateCartAction();

	const active = qtyLabels.find((q) => q.qty === qty) ?? qtyLabels[0];
	const minQty = qtyLabels[0]?.qty ?? 1;
	const maxQty = qtyLabels.at(-1)?.qty ?? minQty;

	const changeQty = (delta: number) => {
		setQty((current) => Math.min(maxQty, Math.max(minQty, current + delta)));
	};

	const handleAdd = () => {
		if (!active) return;
		const formData = new FormData();
		formData.set(POSTHOG_EVENT_CONTEXT_FORM_FIELD, JSON.stringify(getPostHogEventContext()));
		formData.set("channel", channel);
		formData.set("locale", locale);
		formData.set("variantId", variantId);
		formData.set("quantity", String(qty));
		formData.set("productName", "BAC-Water 10 ml");
		formData.set("variantName", `${qty} Stück`);

		gateAction(() => {
			startTransition(async () => {
				await addPdpLineToCheckout(formData);
			});
		});
	};

	return (
		<div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2 sm:flex-nowrap">
			<div className="min-w-[4.8rem] text-left sm:text-right">
				<p
					className="font-display text-[0.95rem] font-medium tabular-nums tracking-[-0.005em] text-primary"
					aria-live="polite"
				>
					{active?.totalLabel ?? ""}
				</p>
			</div>
			<div className="flex h-11 shrink-0 items-center rounded-lg border border-border bg-card">
				<button
					type="button"
					aria-label={t("decreaseQuantity")}
					onClick={() => changeQty(-1)}
					disabled={qty <= minQty}
					className={cn(
						"flex h-full w-9 items-center justify-center rounded-l-lg transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-35",
					)}
				>
					<Minus className="h-3.5 w-3.5" aria-hidden />
				</button>
				<span
					aria-live="polite"
					className="flex h-full w-9 items-center justify-center border-x border-border font-display text-[0.95rem] font-medium tabular-nums"
				>
					{qty}
				</span>
				<button
					type="button"
					aria-label={t("increaseQuantity")}
					onClick={() => changeQty(1)}
					disabled={qty >= maxQty}
					className={cn(
						"flex h-full w-9 items-center justify-center rounded-r-lg transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-35",
					)}
				>
					<Plus className="h-3.5 w-3.5" aria-hidden />
				</button>
			</div>
			<button
				type="button"
				onClick={handleAdd}
				disabled={isPending || !active}
				className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full bg-primary-hero px-4 font-display text-[0.88rem] font-medium tracking-[-0.005em] text-primary-hero-foreground transition-colors hover:bg-primary disabled:opacity-50"
			>
				<Plus className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
				{addLabel}
			</button>
		</div>
	);
}
